/**
 * UserDropdown — Avatar + profile/settings/sign out menu
 *
 * **100% DSL-driven**: Menu items come from LayoutConfig.features.userMenuItems.
 * Icons resolved from shared icon-map registry. Zero hardcoded menu structure.
 */

import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Building2, Network, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useLayoutConfigContext } from '@/lib/use-layout-config';
import { resolveIcon } from '@/lib/icon-map';
import { useAuth } from '@/lib/auth';
import { groupApi, type GroupTenant } from '@/lib/network-api';
import { useAppStore, type Language } from '@/stores/app-store';
import { sanitizeUserDisplay } from '@/lib/user-display';

interface UserDropdownProps {
  user: { name?: string; email?: string; avatarUrl?: string } | null;
  onSignOut: () => void;
  onSettings?: () => void;
  onProfile?: () => void;
  onShortcuts?: () => void;
}

export function UserDropdown({ user, onSignOut, onSettings, onProfile, onShortcuts }: UserDropdownProps) {
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [groupTenants, setGroupTenants] = useState<GroupTenant[]>([]);
  const [groupName, setGroupName] = useState<string>('');
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { config } = useLayoutConfigContext();
  const { tenants, user: authUser, switchTenant, switchGroupTenant } = useAuth();
  const menuItems = config.features.userMenuItems;
  const { language, setLanguage } = useAppStore();
  const langOptions = config.languages;

  // Load group tenants only when the menu is opened; this should not compete with page first paint.
  useEffect(() => {
    if (!open || !authUser?.groupId) { setGroupTenants([]); setGroupName(''); return; }
    groupApi.get(authUser.groupId)
      .then(res => { setGroupTenants(res.tenants ?? []); setGroupName(res.name); })
      .catch(() => { setGroupTenants([]); setGroupName(''); });
  }, [authUser?.groupId, open]);

  const actionHandlers: Record<string, (() => void) | undefined> = {
    profile: onProfile,
    settings: onSettings,
    shortcuts: onShortcuts,
    signout: onSignOut,
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const displayName = sanitizeUserDisplay(user?.name);
  const initials = user?.name
    ? sanitizeUserDisplay(user.name).split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : 'U';

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-lg px-2 py-1 hover:bg-accent transition-colors"
      >
        {user?.avatarUrl ? (
          <img src={user.avatarUrl} alt={user.name ?? 'User'} className="h-7 w-7 rounded-full object-cover" />
        ) : (
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
            {initials}
          </div>
        )}
        <ChevronDown className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform duration-200', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-64 rounded-lg border bg-popover text-popover-foreground shadow-lg py-1 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
          {/* User Info */}
          <div className="px-4 py-3 border-b">
            <div className="flex items-center gap-3">
              {user?.avatarUrl ? (
                <img src={user.avatarUrl} alt={user.name ?? 'User'} className="h-10 w-10 rounded-full object-cover" />
              ) : (
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground">
                  {initials}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{displayName || 'User'}</p>
              </div>
            </div>
          </div>

          {/* Tenant Switcher removed as per architecture switch */}

          {/* Menu Items — DSL driven */}
          <div className="py-1">
            {menuItems.filter(m => m.action !== 'signout').map((item) => {
              const ItemIcon = resolveIcon(item.icon);
              return (
                <div key={item.id}>
                  {item.dividerBefore && <div className="border-t my-1" />}
                  <button
                    onClick={() => { setOpen(false); actionHandlers[item.action]?.(); }}
                    className="flex items-center gap-3 w-full px-4 py-2 text-sm hover:bg-accent transition-colors"
                  >
                    <ItemIcon className="h-4 w-4 text-muted-foreground" />
                    <span>{t(item.labelKey)}</span>
                    {item.shortcut && (
                      <kbd className="ml-auto text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{item.shortcut}</kbd>
                    )}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Language Picker — inline in user menu */}
          {langOptions.length > 1 && (
            <>
              <div className="border-t my-1" />
              <div className="px-4 py-2">
                <div className="flex items-center gap-2 mb-1.5">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('theme.languageLabel', 'Language')}</span>
                </div>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value as Language)}
                  className="w-full rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer"
                >
                  {langOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          <div className="border-t my-1" />

          {/* Sign Out — DSL driven */}
          <div className="py-1">
            {menuItems.filter(m => m.action === 'signout').map((item) => {
              const ItemIcon = resolveIcon(item.icon);
              return (
                <button
                  key={item.id}
                  onClick={() => { setOpen(false); onSignOut(); }}
                  className="flex items-center gap-3 w-full px-4 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors"
                >
                  <ItemIcon className="h-4 w-4" />
                  <span>{t(item.labelKey)}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
